import { createSlice, createEntityAdapter, PayloadAction } from '@reduxjs/toolkit';
import { PromptDto } from '../../../components/types';
import { Slices } from '../slices';
import { PromptsSchema } from './types';

/**
 * EntityAdapter для нормализованного хранения промптов
 */
export const promptsAdapter = createEntityAdapter<PromptDto, number>({
    selectId: prompt => prompt.id,
    sortComparer: (a, b) => {
        // Сортировка по дате создания (новые сначала)
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    },
});

/**
 * Начальное состояние
 */
const initialState: PromptsSchema = promptsAdapter.getInitialState({
    loading: false,
    error: null,
    pagination: {
        nextCursor: null,
        hasMore: true,
    },
    lists: {
        feed: [],
        popular: [],
        my: [],
        similar: {},
    },
});

/**
 * Slice для управления промптами
 */
const promptsSlice = createSlice({
    name: Slices.Prompts,
    initialState,
    reducers: {
        /**
         * Установить статус загрузки
         */
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
            if (action.payload) {
                state.error = null;
            }
        },

        /**
         * Установить ошибку
         */
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
            state.loading = false;
        },

        /**
         * Добавить промпты в кеш
         */
        addPrompts: (state, action: PayloadAction<PromptDto[]>) => {
            promptsAdapter.upsertMany(state, action.payload);
        },

        /**
         * Обновить промпт в кеше
         */
        updatePrompt: (state, action: PayloadAction<{ id: number; changes: Partial<PromptDto> }>) => {
            promptsAdapter.updateOne(state, action.payload);
        },

        /**
         * Обновить лайки промпта
         */
        updatePromptLikes: (state, action: PayloadAction<{ id: number; likes: number; isLiked: boolean }>) => {
            const { id, likes, isLiked } = action.payload;
            promptsAdapter.updateOne(state, {
                id,
                changes: { likes, isLiked },
            });
        },

        /**
         * Установить список промптов для ленты
         */
        setFeedPrompts: (
            state,
            action: PayloadAction<{ prompts: PromptDto[]; nextCursor: string | null; hasMore: boolean }>,
        ) => {
            const { prompts, nextCursor, hasMore } = action.payload;

            // Добавляем промпты в кеш
            promptsAdapter.upsertMany(state, prompts);

            // Обновляем список ID для ленты
            state.lists.feed = prompts.map(p => p.id);
            state.pagination.nextCursor = nextCursor;
            state.pagination.hasMore = hasMore;
        },

        /**
         * Добавить промпты к существующему списку ленты (для пагинации)
         */
        appendFeedPrompts: (
            state,
            action: PayloadAction<{ prompts: PromptDto[]; nextCursor: string | null; hasMore: boolean }>,
        ) => {
            const { prompts, nextCursor, hasMore } = action.payload;

            // Добавляем промпты в кеш
            promptsAdapter.upsertMany(state, prompts);

            // Добавляем новые ID к существующему списку (дедупликация)
            const newIds = prompts.map(p => p.id);
            const existingIds = new Set(state.lists.feed);
            const uniqueNewIds = newIds.filter(id => !existingIds.has(id));

            state.lists.feed.push(...uniqueNewIds);
            state.pagination.nextCursor = nextCursor;
            state.pagination.hasMore = hasMore;
        },

        /**
         * Установить популярные промпты
         */
        setPopularPrompts: (state, action: PayloadAction<PromptDto[]>) => {
            promptsAdapter.upsertMany(state, action.payload);
            state.lists.popular = action.payload.map(p => p.id);
        },

        /**
         * Установить мои промпты
         */
        setMyPrompts: (state, action: PayloadAction<PromptDto[]>) => {
            promptsAdapter.upsertMany(state, action.payload);
            state.lists.my = action.payload.map(p => p.id);
        },

        /**
         * Установить похожие промпты для конкретного промпта
         */
        setSimilarPrompts: (state, action: PayloadAction<{ promptId: number; prompts: PromptDto[] }>) => {
            const { promptId, prompts } = action.payload;
            promptsAdapter.upsertMany(state, prompts);
            state.lists.similar[promptId] = prompts.map(p => p.id);
        },

        /**
         * Очистить кеш
         */
        clearCache: state => {
            promptsAdapter.removeAll(state);
            state.lists = {
                feed: [],
                popular: [],
                my: [],
                similar: {},
            };
            state.pagination = {
                nextCursor: null,
                hasMore: true,
            };
            state.error = null;
        },
    },
});

export const promptsActions = promptsSlice.actions;
export const promptsReducer = promptsSlice.reducer;
